import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { WorkflowDto } from 'src/api/FiretrolApiClient';
import { useWorkflowTemplates } from 'src/api/queries/workflows/useWorkflowTemplates';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { formatDate } from 'src/utils';

const getColumns = (): MRT_ColumnDef<WorkflowDto>[] => {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 200,
      Cell: ({ row }) => (
        <Group gap="xs" wrap="nowrap">
          {row.original.name}
          {row.original.isSystem && (
            <Badge color="blue" variant="light">
              System
            </Badge>
          )}
        </Group>
      ),
    },
    {
      id: 'module',
      accessorFn: (row) => row.resourceId?.toUpperCase(),
      header: 'Module',
      size: 100,
    },
    {
      id: 'office',
      header: 'Office',
      accessorFn: (row) => (row.office ? row.office.name : 'Global'),
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => (row.isActive ? 'Active' : 'Inactive'),
      Cell: ({ row }) => {
        const isActive = row.original.isActive;
        return (
          <Badge color={isActive ? 'green' : 'gray'}>
            {isActive ? 'Active' : 'Inactive'}
          </Badge>
        );
      },
    },
    {
      id: 'lastModifiedBy',
      header: 'Last Modified By',
      size: 200,
      accessorFn: (row) => row.lastUpdatedBy?.fullName ?? '',
    },
    {
      id: 'updatedAt',
      header: 'Last Modified',
      accessorFn: (row) => row.updatedAt,
      sortingFn: 'datetime',
      enableColumnFilter: false,
      Cell: ({ cell }) => formatDate(cell.getValue<Date | undefined>()),
    },
    {
      id: 'createdBy',
      header: 'Created By',
      size: 200,
      accessorFn: (row) => row.createdBy?.fullName ?? '',
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessorFn: (row) => row.createdAt,
      sortingFn: 'datetime',
      enableColumnFilter: false,
      Cell: ({ cell }) => formatDate(cell.getValue<Date | undefined>()),
    },
  ];
};

export const Workflows: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching, isError } = useWorkflowTemplates();
  const workflows = useMemo(() => data ?? [], [data]);

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'workflows',
    columns: columns,
    defaultSorting: [{ id: 'name', desc: false }],
  });

  const tableExports = useTableExports<WorkflowDto>('workflows', (data) => {
    return data.map((item) => ({
      ['Name']: item.name,
      ['Module']: item.resourceId?.toUpperCase() ?? '',
      ['Office']: item.office?.name ?? 'Global',
      ['Status']: item.isActive ? 'Active' : 'Inactive',
      ['Last Modified By']: item.lastUpdatedBy?.fullName ?? '',
      ['Last Modified']: formatDate(item.updatedAt),
      ['Created By']: item.createdBy?.fullName ?? '',
      ['Created']: formatDate(item.createdAt),
      ['System']: item.isSystem ? 'Yes' : 'No',
    }));
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => (
        <Group>
          <EditActionIcon
            onClick={() => navigate(`/settings/workflows/${row.original.id}`)}
          />
        </Group>
      ),
      enableClickToCopy: false,
      columns: columns,
      data: workflows,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton onClick={() => navigate('/settings/workflows/create')}>
            Create Workflow
          </CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: workflows.length,
    isLoading,
    isFetching,
    isError,
    ...tableExports,
  });

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [{ text: 'Settings' }, { text: 'Workflows' }],
        }}
        title="Workflows"
        helpText="Workflows define automated approval and notification processes for various modules in the system."
      />

      {table}
    </>
  );
};
